import * as yup from "yup";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import Papa from "papaparse";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import GetAppIcon from "@material-ui/icons/GetApp";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";

import Dialog from "../shared/Dialog";
import DialogActions from "../DialogActions";
import useConfirmLeave from "../../hooks/useConfirmLeave";
import useModal from "../../hooks/useModal";
import { getFieldProps, inputProps } from "../utils";
import MultiSelect from "../shared/MultiSelect";
import InfoTooltip from "../InfoTooltip";
import { analyzeUsersListCsv } from "./usersListCsvUtils";
import {
  CDP_COMPANY_ID,
  DIGITALATTITUDE_COMPANY_ID,
  SNAM_COMPANY_ID,
  isCdpOnlyUser,
  isSnamOnlyUser,
} from "../../lib/companyAccess";

const editSchema = yup.object().shape({
  allowedcompanies: yup.array().of(yup.string()).required(),
  listdescription: yup.string().nullable(),
  listname: yup.string().required(),
});

const EditUsersListModal = ({
  item,
  myCompanies,
  myCompaniesIds,
  onClose,
  onConfirm,
  isNew = false,
}) => {
  const { handleConfirm } = useModal({ onClose, onConfirm });
  const companiesAsUser = { companies: myCompaniesIds };
  const isSnamUser = isSnamOnlyUser(companiesAsUser);
  const isCdpUser = isCdpOnlyUser(companiesAsUser);
  const [fileUploadDisplayMessage, setFileUploadDisplayMessage] = useState(
    "Info: needs to have DID"
  );

  const formik = useFormik({
    initialValues: isNew
      ? {
          allowedcompanies: [],
          fulldata: null,
          listdescription: "",
          listname: "",
        }
      : item,
    onSubmit: (values) => {
      const isActuallyNoData =
        !Array.isArray(values?.fulldata) || values.fulldata.length === 0;
      if (isNew && isActuallyNoData) return;
      handleConfirm(values);
    },
    validationSchema: editSchema,
    validator: () => ({}),
  });

  const hasSnamSelected =
    Array.isArray(formik.values?.allowedcompanies) &&
    formik.values.allowedcompanies.includes(SNAM_COMPANY_ID);
  const hasCdpSelected =
    Array.isArray(formik.values?.allowedcompanies) &&
    formik.values.allowedcompanies.includes(CDP_COMPANY_ID);
  const prevHasSnamSelectedRef = useRef(hasSnamSelected);

  // Automatically selecting SNAM when user only has snam as company
  useEffect(() => {
    if (isSnamUser || isCdpUser) {
      const current = formik.values.allowedcompanies || [];
      let specificCompanyId = null;
      if (isSnamUser) specificCompanyId = SNAM_COMPANY_ID;
      if (isCdpUser) specificCompanyId = CDP_COMPANY_ID;
      const next = Array.from(
        new Set([specificCompanyId, DIGITALATTITUDE_COMPANY_ID, ...current])
      );
      if (
        next.length !== current.length ||
        !next.every((id, idx) => id === current[idx])
      ) {
        formik.setFieldValue("allowedcompanies", next);
      }
    }
  }, [isSnamUser, isCdpUser, formik, formik.values.allowedcompanies]);

  // Updating hint text based on allowedcompanies
  useEffect(() => {
    const ids = formik.values?.allowedcompanies || [];
    setFileUploadDisplayMessage((prev) => {
      if (typeof prev === "string" && prev.includes("records loaded"))
        return prev;
      return ids.includes(SNAM_COMPANY_ID)
        ? "Info: needs to have DID or email column"
        : ids.includes(CDP_COMPANY_ID)
        ? "Info: needs to have DID (populated with Microsoft oid)"
        : "Info: needs to have DID";
    });
  }, [formik.values?.allowedcompanies]);

  // Re-validating loaded data if SNAM becomes selected after upload
  useEffect(() => {
    const justSelectedSnam = !prevHasSnamSelectedRef.current && hasSnamSelected;
    prevHasSnamSelectedRef.current = hasSnamSelected;

    if (
      !justSelectedSnam ||
      !Array.isArray(formik.values?.fulldata) ||
      formik.values.fulldata.length === 0
    ) {
      return;
    }

    try {
      const { data, errorMessage, infoMessage } = analyzeUsersListCsv(
        formik.values.fulldata,
        { hasSnamCompany: true }
      );

      if (errorMessage) {
        formik.setFieldValue("fulldata", null);
        setFileUploadDisplayMessage(errorMessage);
        return;
      }

      if (data) {
        formik.setFieldValue("fulldata", data);
        setFileUploadDisplayMessage(
          infoMessage || `${data.length} records loaded`
        );
      }
    } catch (error) {
      console.error("Error re-validating CSV after SNAM selection => ", error);
      formik.setFieldValue("fulldata", null);
      setFileUploadDisplayMessage("Could not load the file, or no data");
    }
  }, [hasSnamSelected, formik]);

  const handleFileContentChecks = useCallback(
    (rows) => {
      try {
        const { data, errorMessage, infoMessage } = analyzeUsersListCsv(rows, {
          hasCdpCompany: hasCdpSelected,
          hasSnamCompany: hasSnamSelected,
        });

        if (errorMessage) {
          formik.setFieldValue("fulldata", null);
          setFileUploadDisplayMessage(errorMessage);
          return;
        }

        if (data) {
          formik.setFieldValue("fulldata", data);
          setFileUploadDisplayMessage(
            infoMessage || `${data.length} records loaded`
          );
        }
      } catch (error) {
        console.error("Error in handleFileContentChecks => ", error);
        formik.setFieldValue("fulldata", null);
        setFileUploadDisplayMessage("Could not load the file, or no data");
      }
    },
    [formik, hasSnamSelected, hasCdpSelected]
  );

  const handleCompaniesChange = useCallback(
    (value) => {
      formik.setFieldValue("allowedcompanies", value);
      formik.setFieldTouched("allowedcompanies", true);
    },
    [formik]
  );

  const handleFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        Papa.parse(file, {
          complete: (results) => {
            handleFileContentChecks(results?.data);
          },
          header: true,
          skipEmptyLines: true,
        });
      }
    },
    [handleFileContentChecks]
  );

  const handleDownloadSampleFile = useCallback(() => {
    const isSnam = hasSnamSelected;

    let headers;
    let rows;

    if (isSnam) {
      // For SNAM: sample with only email column
      headers = ["email"];
      rows = [];
    } else {
      // For non-SNAM: sample with only DID column
      headers = ["DID"];
      rows = [
        ["c3e57852-c31f-4870-bc75-69148831483c"],
        ["26124244-d095-4f59-8382-e01e70c95ae9"],
      ];
    }

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_users_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [hasSnamSelected]);

  const { onClose: handleClose } = useConfirmLeave({
    formik,
    onClose,
  });

  const hasNoData =
    !Array.isArray(formik.values?.fulldata) ||
    formik.values.fulldata.length === 0;

  const companyOptions = myCompaniesIds.map((id) => ({
    id,
    name: myCompanies?.[id]?.cname || id,
  }));

  return (
    <Dialog fullWidth open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          {isNew ? "Add Users List" : "Edit Users List"}
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4} style={{ alignItems: "center" }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              InputProps={inputProps}
              label="List Name"
              margin="none"
              placeholder="Name of the users list"
              variant="outlined"
              {...getFieldProps(formik, { name: "listname" })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={inputProps}
              label="Description"
              margin="none"
              placeholder="Description of the users list"
              variant="outlined"
              {...getFieldProps(formik, { name: "listdescription" })}
            />
          </Grid>

          {isNew && (
            <Grid item xs={12}>
              <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
              >
                <Box alignItems="center" display="flex">
                  <input
                    accept=".csv"
                    id="csv-file-upload"
                    style={{ display: "none" }}
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <Box
                    alignItems="center"
                    display="inline-flex"
                    position="relative"
                  >
                    <label htmlFor="csv-file-upload">
                      <Button
                        color="primary"
                        component="span"
                        endIcon={<InsertDriveFileIcon />}
                        // styles={{ width: 200 }}
                        variant="outlined"
                      >
                        Upload CSV File
                      </Button>
                    </label>
                    <Box position="absolute" right={-18} top={-18}>
                      <InfoTooltip
                        text="Uploading a new file will overwrite the current list"
                        title="Upload behavior"
                      />
                    </Box>
                  </Box>
                  <Box
                    alignItems="center"
                    display="inline-flex"
                    ml={6}
                    position="relative"
                  >
                    <Button
                      color="primary"
                      endIcon={<GetAppIcon />}
                      variant="outlined"
                      onClick={handleDownloadSampleFile}
                    >
                      Download Sample File
                    </Button>
                    <Box position="absolute" right={-18} top={-18}>
                      <InfoTooltip
                        text={
                          hasSnamSelected
                            ? "CSV must contain a DID column or an email column or both."
                            : "CSV must contain a DID column."
                        }
                        title="CSV requirements"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              {fileUploadDisplayMessage && (
                <Box mt={1}>
                  <Typography color="textSecondary" variant="body2">
                    {fileUploadDisplayMessage}
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
          {!isSnamUser && !isCdpUser && (
            <Grid item xs={12}>
              <MultiSelect
                fullWidth
                required
                items={companyOptions}
                label="Companies"
                placeholder="Select companies"
                {...getFieldProps(formik, { name: "allowedcompanies" })}
                onChange={handleCompaniesChange}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={
            !formik.dirty || formik.isSubmitting || (isNew && hasNoData)
          }
          endIcon={formik.isSubmitting && <CircularProgress size={25} />}
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Save
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUsersListModal;
